import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import { motion } from "framer-motion";
import { pageMeta } from "@/data/mockData";
import { useTestimonials } from "@/lib/content";

export default function Testimonials() {
  const { data: testimonials } = useTestimonials();
  return (
    <>
      <SEO {...pageMeta.testimonials} />

      <section className="bg-white pt-20 pb-12 md:py-24" data-testid="testimonials-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Testimonials
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.05]">
              What clients say.
            </h1>
            <p className="mt-7 text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Real homeowners, investors, and upgraders who have worked with Johnny — captured at the moment of handover.
            </p>
          </Reveal>
        </div>
      </section>

      <TestimonialCarousel />

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* BACKEND INTEGRATION: replace `testimonials` with fetch to /api/testimonials. */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {testimonials.map((t, i) => (
              <motion.article
                key={t.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group bg-white border border-gray-100 rounded-3xl overflow-hidden hover:shadow-xl transition-all flex flex-col"
                data-testid={`testimonial-card-${t.id}`}
              >
                {t.photo && (
                  /* 16:9 box, object-contain so portrait certificates (A4) show in full */
                  <div className="aspect-[16/9] overflow-hidden bg-gray-50 flex items-center justify-center border-b border-gray-100">
                    <img
                      src={t.photo}
                      alt={`${t.name} — testimonial`}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <div className="p-7 md:p-8 border-l-2 border-[#ffde59] flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    {t.avatar && (
                      <img
                        src={t.avatar}
                        alt={t.name}
                        loading="lazy"
                        className="h-11 w-11 rounded-full object-cover ring-2 ring-[#ffde59]/50"
                      />
                    )}
                    <div>
                      <div className="font-serif text-[#0a2540] text-lg leading-tight">{t.name}</div>
                      {t.role && (
                        <div className="text-xs text-slate-500 uppercase tracking-[0.18em] mt-0.5">{t.role}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <i className="fa-solid fa-quote-left text-2xl text-[#ffde59] mb-3 block" aria-hidden="true" />
                    {t.title && (
                      <p className="font-bold text-[#0a2540] mb-3">
                        {t.title}
                      </p>
                    )}
                    <p className="text-base md:text-lg text-slate-700 leading-relaxed italic">
                      "{t.quote}"
                    </p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <Reveal className="mt-14 text-center">
            <p className="text-sm text-slate-500 max-w-2xl mx-auto">
              More client stories are being added regularly. Want yours featured? Drop Johnny a message after your transaction.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
